// import node module libraries
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';

// import custom components
// import bootstrap icons
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { useEffect, useState } from 'react';
import PersonnelService from '../../services/PersonnelService';
import BulletinpaieService from '../../services/BulletinpaieService';
import ContratService from '../../services/ContratService';

const OffcanvasAddBulletinpaieForm = ({ onClick }) => {
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [personnelOptions, setPersonnelOptions] = useState([]);

	useEffect(() => {
		const fetchPersonnelOptions = async () => {
			try {
				const personnels = await PersonnelService.getPersonnelOptions();
				setPersonnelOptions(personnels);
			} catch (error) {
				console.error('Error fetching personnels options', error);
			}
		};

		fetchPersonnelOptions();
	}, []);

	const PERSONNEL_CHOICES = personnelOptions;

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const bulletinpaieData = new FormData();

				// Add all your form data to the FormData
				bulletinpaieData.append('date_paiement', form.date_paiement.value);
				bulletinpaieData.append(
					'heures_supplementaires',
					form.heures_supplementaires.value
				);
				bulletinpaieData.append(
					'taux_heures_supplementaires',
					form.taux_heures_supplementaires.value
				);

				const personnel = form.personnel.value;

				// Use await to get the contract data before appending it to FormData
				const contrat = await ContratService.getContratByPersonnelId(personnel);
				const contrat_id = contrat[0].id;

				bulletinpaieData.append('indemnites', form.indemnites.value);
				bulletinpaieData.append('retenues', form.retenues.value);
				bulletinpaieData.append('personnel', personnel);
				bulletinpaieData.append('contrat', contrat_id); // Append the contract ID, not the whole contract object

				// Call the BulletinpaieService to create the bulletinpaie
				const response = await BulletinpaieService.createBulletinpaie(
					bulletinpaieData
				);

				// Reset the form and state variables after successful creation
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Redirect the user to the list of bulletinpaies
				window.location.href = '/administration/personnel/salaires';
			} catch (error) {
				console.error('Erreur lors de la création du bulletin de paie', error);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<Form
			className="mb-3"
			noValidate
			validated={validated}
			onSubmit={handleSubmit}
		>
			{showAlert && (
				<Alert variant="danger">
					<ExclamationTriangleFill size={25} className="me-1" />
					{alertMessage}
				</Alert>
			)}
			<Row>
				{/* Date paiement */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="date_paiement">
							Date paiement <span className="text-danger">*</span>
						</Form.Label>
						<Form.Control
							name="date_paiement"
							type="date"
							placeholder="Selectionner la date de paiement"
							id="date_paiement"
							required
							isInvalid={!!feedbackMessages['date_paiement']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									date_paiement: e.target.value
										? ''
										: 'La date de paiement de ce salaire est requise.'
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['date_paiement']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Taux heures supplementaires et Heures supplementaires  */}
				<Col xs={12} className="mb-3">
					<Form.Group>
						<Row>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="taux_heures_supplementaires">
									Taux heures supplementaires{' '}
									<span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="taux_heures_supplementaires"
									type="number"
									placeholder="123456789 FCFA"
									id="taux_heures_supplementaires"
									defaultValue="0"
									required
									isInvalid={!!feedbackMessages['taux_heures_supplementaires']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											taux_heures_supplementaires: e.target.value
												? ''
												: "Le taux d'heures supplementaires est requis."
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['taux_heures_supplementaires']}
								</Form.Control.Feedback>
							</Col>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="heures_supplementaires">
									Heures supplementaires
								</Form.Label>
								<Form.Control
									name="heures_supplementaires"
									type="number"
									min="0"
									defaultValue="0"
									placeholder="Heures suppléméntaires"
									id="heures_supplementaires"
									isInvalid={!!feedbackMessages['heures_supplementaires']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											heures_supplementaires: e.target.value
												? ''
												: "La valeur sasie n'est pas valide."
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['heures_supplementaires']}
								</Form.Control.Feedback>
							</Col>
						</Row>
					</Form.Group>
				</Col>

				{/* Indemnités */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="indemnites">Indemnités</Form.Label>
						<Form.Control
							name="indemnites"
							type="number"
							defaultValue="0"
							placeholder="123456789 FCFA"
							id="indemnites"
							isInvalid={!!feedbackMessages['indemnites']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									indemnites: e.target.value
										? ''
										: "La valeur des indemnités n'est pas valide."
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['indemnites']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Retenues */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="retenues">Retenues</Form.Label>
						<Form.Control
							name="retenues"
							type="number"
							defaultValue="0"
							placeholder="123456789 FCFA"
							id="retenues"
							isInvalid={!!feedbackMessages['retenues']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									retenues: e.target.value
										? ''
										: "La valeur des retenues n'est pas valide."
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['retenues']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Personnel */}
				<Col xs={12} className="mb-3">
					<Form.Label htmlFor="personnel">Personnel</Form.Label>
					<Form.Select
						name="personnel"
						required
						isInvalid={!!feedbackMessages['personnel']}
						onChange={(e) => {
							const value = e.target.value;
							const feedbackMessage = value
								? ''
								: 'Selectionner la personne concernée par ce bulletin de paie.';
						}}
					>
						<option value="">
							Selectionner la personne concernée par ce bulletin de paie
						</option>
						{PERSONNEL_CHOICES.map((choice, index) => (
							<option key={index} value={choice.value}>
								{choice.label}
							</option>
						))}
					</Form.Select>
					<Form.Control.Feedback type="invalid">
						{feedbackMessages['personnel']}
					</Form.Control.Feedback>
				</Col>

				{/* Buttons */}
				<Col xs={12} className="mt-4">
					<Button variant="success" type="submit">
						Enregistrer
					</Button>
					<Button onClick={onClick} variant="outline-danger" className="ms-2">
						Fermer
					</Button>
				</Col>
			</Row>
		</Form>
	);
};

export default OffcanvasAddBulletinpaieForm;
